import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search, UsersRound, Video } from 'lucide-react';
import { formatClocks, formatTime } from '@/lib/utils';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export default function FreeSlotsPage() {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({ q: '', subject_id: '', teacher_id: '', location_id: '', day_of_week: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/subjects').then((r) => setSubjects(r.data || []));
    api.get('/teachers', { params: { limit: 100 } }).then((r) => setTeachers(r.data || []));
    api.get('/locations').then((r) => setLocations((r.data || []).filter((l) => l.is_active)));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''));
      api.get('/courses/free-slots', { params })
        .then((r) => setData(r.data))
        .catch(() => toast.error('Freie Plätze konnten nicht geladen werden'))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [filters]);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const selCls = 'h-9 rounded-md border border-slate-300 text-xs px-2 bg-white max-w-[220px]';

  return (
    <Layout>
      <div className="p-6 space-y-4" data-testid="free-slots-page">
        <div className="flex items-center gap-3">
          <UsersRound className="w-6 h-6 text-brand-red" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Freie Plätze</h1>
            <p className="text-xs text-slate-500">Gruppenkurse mit freien Plätzen — ideal für die Einteilung neuer Schüler</p>
          </div>
          {data && (
            <Badge className="ml-auto bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-100" data-testid="free-slots-total">
              {data.total_free_slots} freie Plätze in {data.courses.length} Kursen
            </Badge>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input value={filters.q} onChange={(e) => set('q', e.target.value)}
                   placeholder="Kursname..." className="pl-8 h-9 text-xs w-48" data-testid="free-slots-q" />
          </div>
          <select value={filters.subject_id} onChange={(e) => set('subject_id', e.target.value)} className={selCls} data-testid="free-slots-subject">
            <option value="">Alle Fächer</option>
            {subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select value={filters.teacher_id} onChange={(e) => set('teacher_id', e.target.value)} className={selCls} data-testid="free-slots-teacher">
            <option value="">Alle Lehrkräfte</option>
            {teachers.map((t) => <option key={t.id} value={t.id}>{t.last_name}, {t.first_name}</option>)}
          </select>
          <select value={filters.location_id} onChange={(e) => set('location_id', e.target.value)} className={selCls} data-testid="free-slots-location">
            <option value="">Alle Standorte</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
          <div className="flex rounded-md border border-slate-200 overflow-hidden">
            {WEEKDAYS.map((d, idx) => (
              <button key={d} onClick={() => set('day_of_week', filters.day_of_week === String(idx) ? '' : String(idx))}
                      data-testid={`free-slots-day-${d}`}
                      className={`px-2.5 py-1.5 text-xs font-semibold border-r last:border-r-0 border-slate-200 transition-colors ${filters.day_of_week === String(idx) ? 'bg-brand-red text-white' : 'bg-white text-slate-600 hover:bg-brand-red/5'}`}>
                {d}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {loading && !data ? (
            <div className="py-16 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red" /></div>
          ) : !data || data.courses.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-400" data-testid="free-slots-empty">Keine Gruppenkurse mit freien Plätzen gefunden</div>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[10px] uppercase text-slate-500">
                  <th className="text-left py-2 px-3 font-medium">Kurs</th>
                  <th className="text-left py-2 px-3 font-medium">Fach</th>
                  <th className="text-left py-2 px-3 font-medium">Lehrkraft</th>
                  <th className="text-left py-2 px-3 font-medium">Termin</th>
                  <th className="text-left py-2 px-3 font-medium">Standort / Raum</th>
                  <th className="text-right py-2 px-3 font-medium w-24">Belegung</th>
                  <th className="text-right py-2 px-3 font-medium w-20">Frei</th>
                </tr>
              </thead>
              <tbody>
                {data.courses.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/courses/${c.id}`)}
                      className="border-b border-slate-100 hover:bg-brand-red/5 cursor-pointer"
                      data-testid={`free-slot-row-${c.id}`}>
                    <td className="py-2 px-3 font-medium">
                      {formatClocks(c.name) || c.name}
                      {c.is_online && <Video className="inline w-3 h-3 ml-1.5 text-emerald-600" />}
                    </td>
                    <td className="py-2 px-3 text-slate-600">{c.subject_name || '—'}</td>
                    <td className="py-2 px-3 text-slate-600">{c.teacher_name || '—'}</td>
                    <td className="py-2 px-3 text-slate-600">
                      {c.day_of_week != null ? WEEKDAYS[c.day_of_week] : ''} {formatTime(c.start_time) || ''}
                      {c.duration_minutes ? ` · ${c.duration_minutes} Min` : ''}
                    </td>
                    <td className="py-2 px-3 text-slate-600">
                      {[c.location_name, c.room_name].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td className="py-2 px-3 text-right font-mono">{c.enrolled}/{c.capacity}</td>
                    <td className="py-2 px-3 text-right">
                      <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-100 font-mono">
                        {c.free}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
